/**
 * MapRenderer - Main PixiJS renderer for the map viewport
 *
 * Maps are up to 1000x1000 tiles (32000px square), far beyond what a single
 * texture or a sprite per tile can handle, so the three layers are built for
 * the viewport rather than the map:
 *
 * - Base tiles are baked into 16x16 tile chunk textures. Only chunks on screen
 *   are baked, a small cache of off-screen chunks is kept, and editing a tile
 *   just marks its chunk dirty so it is rebaked on the next frame.
 * - Objects stay live sprites (they overhang their tile, so baking them into
 *   chunks would clip them). They are grouped into one container per map row,
 *   which draws them in the game's back-to-front order and lets whole rows be
 *   hidden when off screen.
 * - Property overlays and the cursor are Graphics redrawn for the visible area.
 */

import {
    Application,
    Container,
    Graphics,
    Sprite,
    Texture,
    RenderTexture,
    FederatedPointerEvent,
} from 'pixi.js';
import { TILE_SIZE, isWaterTile } from '@/formats/types';

/** Tiles per chunk side */
const CHUNK_TILES = 16;
const CHUNK_PIXELS = CHUNK_TILES * TILE_SIZE;

/** Off-screen chunks kept baked so panning back is free (1MB of VRAM each) */
const MAX_IDLE_CHUNKS = 64;

/** Time per frame spent baking chunks before deferring the rest */
const BAKE_BUDGET_MS = 8;

/** The overlay is redrawn when the view moves this many tiles */
const OVERLAY_SNAP_TILES = 8;

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;

export interface TileBounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

/** Tile data the property overlay needs */
export interface OverlayTile {
    tileSprite: number;
    blocked: boolean;
    teleport: boolean;
    farming: boolean;
    buildable: boolean;
}

export interface OverlayVisibility {
    blocked: boolean;
    teleport: boolean;
    farming: boolean;
    buildable: boolean;
    water: boolean;
}

/** What to bake for a base tile */
export interface BaseTile {
    /** Undefined until the tile's spritesheet has loaded */
    texture: Texture | null | undefined;
    /**
     * Color key of the tile's spritesheet. Spritesheets have their key color
     * made transparent for the object layer, but the game draws base tiles
     * opaque, so the key is painted back in behind them.
     */
    colorKey: number | null;
}

/** A tile in the cursor preview */
export interface CursorTile {
    offsetX: number;
    offsetY: number;
    texture?: Texture | null;
    pivotX?: number;
    pivotY?: number;
}

export interface MapRendererOptions {
    /** Container element to mount the renderer */
    container: HTMLElement;
    /** Base tile at a map position */
    getBaseTile: (x: number, y: number) => BaseTile | null;
    /** Tile data at a map position, for the property overlay */
    getOverlayTile: (x: number, y: number) => OverlayTile | null;
    /** Callback when the mouse moves over a tile (-1, -1 when it leaves) */
    onTileHover?: (x: number, y: number) => void;
    /** Callback when a tile is right-clicked */
    onTileRightClick?: (x: number, y: number) => void;
    /** Callback when a left-button drag starts */
    onDragStart?: (x: number, y: number) => void;
    /** Callback when a drag reaches a different tile */
    onDrag?: (x: number, y: number) => void;
    /** Callback when dragging ends */
    onDragEnd?: (x: number, y: number) => void;
    /** Callback when the user pans or zooms */
    onViewChange?: (panX: number, panY: number, zoom: number) => void;
    /** Callback when the viewport is resized */
    onResize?: (width: number, height: number) => void;
}

interface TileChunk {
    sprite: Sprite;
    texture: RenderTexture;
    chunkX: number;
    chunkY: number;
    dirty: boolean;
    lastVisibleFrame: number;
}

const OVERLAY_STYLES: Array<{ key: keyof OverlayVisibility; color: number; alpha: number }> = [
    { key: 'water', color: 0x3b82f6, alpha: 0.4 },
    { key: 'buildable', color: 0xf97316, alpha: 0.4 },
    { key: 'farming', color: 0x14b8a6, alpha: 0.45 },
    { key: 'blocked', color: 0xff4444, alpha: 0.4 },
    { key: 'teleport', color: 0x22c55e, alpha: 0.55 },
];

function overlayTileHas(tile: OverlayTile, key: keyof OverlayVisibility): boolean {
    return key === 'water' ? isWaterTile(tile) : tile[key];
}

export class MapRenderer {
    private app: Application;
    private container: HTMLElement;
    private mapWidth: number = 0;
    private mapHeight: number = 0;

    // Display layers
    private worldContainer: Container;
    private chunkLayer: Container;
    private objectLayer: Container;
    private propertyOverlay: Graphics;
    private gridLayer: Graphics;
    private selectionOverlay: Graphics;
    private cursorOverlay: Graphics;
    private cursorPreviewContainer: Container;
    private cursorPreviewSprites: Sprite[] = [];

    // Tile chunks
    private chunks: Map<string, TileChunk> = new Map();
    private freeChunkTextures: RenderTexture[] = [];
    private bakeContainer: Container;
    private bakeSprites: Sprite[] = [];
    private bakeBackgrounds: Sprite[] = [];

    // Objects
    private objectRows: Container[] = [];
    private objectSprites: Map<number, Sprite> = new Map();
    // How far object sprites reach above / below their tile, in pixels
    private objectReachUp: number = 0;
    private objectReachDown: number = TILE_SIZE;

    // Overlay
    private overlayVisibility: OverlayVisibility = {
        blocked: false,
        teleport: false,
        farming: false,
        buildable: false,
        water: false,
    };
    private overlayDirty: boolean = true;
    private lastOverlayBounds: string = '';

    // View state
    private _zoom: number = 1;
    private _panX: number = 0;
    private _panY: number = 0;
    private frame: number = 0;
    private lastObjectRowRange: string = '';

    // Interaction state
    private isDragging: boolean = false;
    private isPanning: boolean = false;
    private panMode: boolean = false;
    private lastMouseX: number = 0;
    private lastMouseY: number = 0;
    private lastDragTileX: number = -1;
    private lastDragTileY: number = -1;

    private options: MapRendererOptions;
    private resizeObserver: ResizeObserver | null = null;
    private showGrid: boolean = true;
    private destroyed: boolean = false;

    private readonly handleWheelBound = (event: WheelEvent): void => this.handleWheel(event);
    private readonly handleContextMenuBound = (event: Event): void => event.preventDefault();
    private readonly handlePointerLeaveBound = (): void => this.handlePointerLeave();
    private readonly handleWindowBlurBound = (): void => this.endInteraction();

    constructor(options: MapRendererOptions) {
        this.options = options;
        this.container = options.container;

        // Create PixiJS application
        this.app = new Application();

        // Create display layers
        this.worldContainer = new Container();
        this.chunkLayer = new Container();
        this.objectLayer = new Container();
        this.propertyOverlay = new Graphics();
        this.gridLayer = new Graphics();
        this.selectionOverlay = new Graphics();
        this.cursorOverlay = new Graphics();
        this.cursorPreviewContainer = new Container();
        this.cursorPreviewContainer.alpha = 0.8;

        this.worldContainer.addChild(this.chunkLayer);
        this.worldContainer.addChild(this.objectLayer);
        this.worldContainer.addChild(this.propertyOverlay);
        this.worldContainer.addChild(this.gridLayer);
        this.worldContainer.addChild(this.selectionOverlay);
        this.worldContainer.addChild(this.cursorPreviewContainer);
        this.worldContainer.addChild(this.cursorOverlay);

        // Reusable sprites for baking one chunk at a time: a color key
        // background per tile, with the tile sprites on top
        this.bakeContainer = new Container();
        const backgrounds = new Container();
        const tiles = new Container();
        this.bakeContainer.addChild(backgrounds, tiles);

        for (let ty = 0; ty < CHUNK_TILES; ty++) {
            for (let tx = 0; tx < CHUNK_TILES; tx++) {
                const background = new Sprite(Texture.WHITE);
                background.x = tx * TILE_SIZE;
                background.y = ty * TILE_SIZE;
                background.width = TILE_SIZE;
                background.height = TILE_SIZE;
                backgrounds.addChild(background);
                this.bakeBackgrounds.push(background);

                const sprite = new Sprite();
                sprite.x = tx * TILE_SIZE;
                sprite.y = ty * TILE_SIZE;
                tiles.addChild(sprite);
                this.bakeSprites.push(sprite);
            }
        }
    }

    async init(): Promise<void> {
        await this.app.init({
            resizeTo: this.container,
            backgroundColor: 0x1a1a2e,
            antialias: false,
            // Off: adjacent chunks must share exact edges or seams show when zoomed
            roundPixels: false,
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
        });

        this.container.appendChild(this.app.canvas);
        this.app.stage.addChild(this.worldContainer);

        // Set up event handling
        this.app.stage.eventMode = 'static';
        this.app.stage.hitArea = this.app.screen;

        this.app.stage.on('pointermove', this.handlePointerMove, this);
        this.app.stage.on('pointerdown', this.handlePointerDown, this);
        this.app.stage.on('pointerup', this.handlePointerUp, this);
        this.app.stage.on('pointerupoutside', this.handlePointerUp, this);

        // Native listeners: PixiJS registers wheel as passive, which can't stop
        // the browser's own ctrl+wheel zoom
        this.app.canvas.addEventListener('wheel', this.handleWheelBound, { passive: false });
        this.app.canvas.addEventListener('contextmenu', this.handleContextMenuBound);
        this.app.canvas.addEventListener('pointerleave', this.handlePointerLeaveBound);
        window.addEventListener('blur', this.handleWindowBlurBound);

        // The viewport also resizes when a sidebar is dragged, not just the window
        this.resizeObserver = new ResizeObserver(() => {
            if (this.destroyed) return;
            this.app.resize();
            this.options.onResize?.(this.app.screen.width, this.app.screen.height);
        });
        this.resizeObserver.observe(this.container);

        this.app.ticker.add(this.update, this);
        this.options.onResize?.(this.app.screen.width, this.app.screen.height);
    }

    /**
     * Set map dimensions, discarding everything rendered for the previous map
     */
    setMapSize(width: number, height: number): void {
        this.clear();

        this.mapWidth = width;
        this.mapHeight = height;

        for (let y = 0; y < height; y++) {
            const row = new Container();
            row.visible = false;
            this.objectRows.push(row);
            this.objectLayer.addChild(row);
        }

        this.updateGrid();
    }

    /**
     * Remove all rendered map content
     */
    private clear(): void {
        for (const chunk of this.chunks.values()) {
            this.releaseChunk(chunk);
        }
        this.chunks.clear();

        this.objectLayer.removeChildren().forEach(row => row.destroy({ children: true }));
        this.objectRows = [];
        this.objectSprites.clear();
        this.objectReachUp = 0;
        this.objectReachDown = TILE_SIZE;
        this.lastObjectRowRange = '';

        this.propertyOverlay.clear();
        this.selectionOverlay.clear();
        this.overlayDirty = true;
        this.updateCursor(-1, -1);
    }

    // ---- Base tiles -------------------------------------------------------

    /**
     * Redraw the base tile at a position on the next frame
     */
    invalidateTile(x: number, y: number): void {
        const chunk = this.chunks.get(`${Math.floor(x / CHUNK_TILES)},${Math.floor(y / CHUNK_TILES)}`);
        if (chunk) {
            chunk.dirty = true;
        }
        this.overlayDirty = true;
    }

    /**
     * Redraw every base tile (new textures became available)
     */
    invalidateAllTiles(): void {
        for (const chunk of this.chunks.values()) {
            chunk.dirty = true;
        }
    }

    private createChunk(chunkX: number, chunkY: number): TileChunk {
        const texture = this.freeChunkTextures.pop() ?? RenderTexture.create({
            width: CHUNK_PIXELS,
            height: CHUNK_PIXELS,
            resolution: 1,
        });

        const sprite = new Sprite(texture);
        sprite.x = chunkX * CHUNK_PIXELS;
        sprite.y = chunkY * CHUNK_PIXELS;
        this.chunkLayer.addChild(sprite);

        return { sprite, texture, chunkX, chunkY, dirty: true, lastVisibleFrame: this.frame };
    }

    private releaseChunk(chunk: TileChunk): void {
        this.chunkLayer.removeChild(chunk.sprite);
        chunk.sprite.destroy();
        this.freeChunkTextures.push(chunk.texture);
    }

    private bakeChunk(chunk: TileChunk): void {
        const baseX = chunk.chunkX * CHUNK_TILES;
        const baseY = chunk.chunkY * CHUNK_TILES;

        let i = 0;
        for (let ty = 0; ty < CHUNK_TILES; ty++) {
            for (let tx = 0; tx < CHUNK_TILES; tx++) {
                const sprite = this.bakeSprites[i];
                const background = this.bakeBackgrounds[i];
                i++;

                const mapX = baseX + tx;
                const mapY = baseY + ty;

                const tile = mapX < this.mapWidth && mapY < this.mapHeight
                    ? this.options.getBaseTile(mapX, mapY)
                    : null;

                sprite.visible = !!tile?.texture;
                if (tile?.texture) {
                    sprite.texture = tile.texture;
                }

                // No background until the texture is there, so a tile that is
                // still loading reads as missing rather than as a flat color
                background.visible = !!tile?.texture && tile.colorKey !== null;
                if (background.visible) {
                    background.tint = tile!.colorKey!;
                }
            }
        }

        this.app.renderer.render({
            container: this.bakeContainer,
            target: chunk.texture,
            clear: true,
        });
        chunk.dirty = false;
    }

    /**
     * Make sure the chunks on screen exist and are up to date
     */
    private updateChunks(bounds: TileBounds): void {
        const minChunkX = Math.floor(bounds.minX / CHUNK_TILES);
        const minChunkY = Math.floor(bounds.minY / CHUNK_TILES);
        const maxChunkX = Math.floor(bounds.maxX / CHUNK_TILES);
        const maxChunkY = Math.floor(bounds.maxY / CHUNK_TILES);

        const bakeDeadline = performance.now() + BAKE_BUDGET_MS;
        let visibleCount = 0;

        for (let chunkY = minChunkY; chunkY <= maxChunkY; chunkY++) {
            for (let chunkX = minChunkX; chunkX <= maxChunkX; chunkX++) {
                const key = `${chunkX},${chunkY}`;
                let chunk = this.chunks.get(key);
                if (!chunk) {
                    chunk = this.createChunk(chunkX, chunkY);
                    this.chunks.set(key, chunk);
                }

                chunk.lastVisibleFrame = this.frame;
                visibleCount++;

                // Always bake at least one chunk per frame so progress is guaranteed
                if (chunk.dirty && (visibleCount === 1 || performance.now() < bakeDeadline)) {
                    this.bakeChunk(chunk);
                }

                // Unbaked chunks stay hidden rather than showing stale pixels
                chunk.sprite.visible = !chunk.dirty;
            }
        }

        this.evictChunks(visibleCount);
    }

    private evictChunks(visibleCount: number): void {
        let excess = this.chunks.size - visibleCount - MAX_IDLE_CHUNKS;
        if (excess <= 0) return;

        const idle = [...this.chunks.entries()]
            .filter(([, chunk]) => chunk.lastVisibleFrame !== this.frame)
            .sort((a, b) => a[1].lastVisibleFrame - b[1].lastVisibleFrame);

        for (const [key, chunk] of idle) {
            if (excess-- <= 0) break;
            this.releaseChunk(chunk);
            this.chunks.delete(key);
        }
    }

    // ---- Objects ----------------------------------------------------------

    /**
     * Set or clear the object sprite at a position
     */
    setObjectSprite(
        x: number,
        y: number,
        texture: Texture | null | undefined,
        pivotX: number = 0,
        pivotY: number = 0
    ): void {
        if (x < 0 || x >= this.mapWidth || y < 0 || y >= this.mapHeight) return;

        const key = y * this.mapWidth + x;
        let sprite = this.objectSprites.get(key);

        if (!texture) {
            if (sprite) {
                sprite.destroy();
                this.objectSprites.delete(key);
            }
            return;
        }

        if (!sprite) {
            sprite = new Sprite(texture);
            this.objectRows[y].addChild(sprite);
            this.objectSprites.set(key, sprite);
        } else if (sprite.texture !== texture) {
            sprite.texture = texture;
        }

        sprite.x = x * TILE_SIZE + pivotX;
        sprite.y = y * TILE_SIZE + pivotY;

        // Rows are shown by how far their sprites can reach into the view
        const reachUp = -pivotY;
        const reachDown = pivotY + texture.height;
        if (reachUp > this.objectReachUp || reachDown > this.objectReachDown) {
            this.objectReachUp = Math.max(this.objectReachUp, reachUp);
            this.objectReachDown = Math.max(this.objectReachDown, reachDown);
            this.lastObjectRowRange = '';
        }
    }

    private updateObjectRows(bounds: TileBounds): void {
        const firstRow = Math.max(0, bounds.minY - Math.ceil(this.objectReachDown / TILE_SIZE));
        const lastRow = Math.min(this.mapHeight - 1, bounds.maxY + Math.ceil(this.objectReachUp / TILE_SIZE));

        const range = `${firstRow},${lastRow}`;
        if (range === this.lastObjectRowRange) return;
        this.lastObjectRowRange = range;

        for (let y = 0; y < this.objectRows.length; y++) {
            this.objectRows[y].visible = y >= firstRow && y <= lastRow;
        }
    }

    // ---- Overlays ---------------------------------------------------------

    /**
     * Update grid overlay
     */
    private updateGrid(): void {
        this.gridLayer.clear();
        if (!this.showGrid || this.mapWidth === 0 || this.mapHeight === 0) return;

        const width = this.mapWidth * TILE_SIZE;
        const height = this.mapHeight * TILE_SIZE;

        // Vertical lines
        for (let x = 0; x <= this.mapWidth; x++) {
            this.gridLayer.moveTo(x * TILE_SIZE, 0);
            this.gridLayer.lineTo(x * TILE_SIZE, height);
        }

        // Horizontal lines
        for (let y = 0; y <= this.mapHeight; y++) {
            this.gridLayer.moveTo(0, y * TILE_SIZE);
            this.gridLayer.lineTo(width, y * TILE_SIZE);
        }

        // pixelLine keeps the grid 1 screen pixel wide at every zoom
        this.gridLayer.stroke({ width: 1, color: 0x404040, alpha: 0.5, pixelLine: true });
    }

    /**
     * Choose which property overlays are drawn
     */
    setOverlayVisibility(visibility: OverlayVisibility): void {
        this.overlayVisibility = { ...visibility };
        this.overlayDirty = true;
    }

    /**
     * Redraw the property overlays on the next frame
     */
    invalidateOverlay(): void {
        this.overlayDirty = true;
    }

    /**
     * Draw property overlays for the visible area. Horizontal runs of flagged
     * tiles are merged into one rectangle: blocked regions cover most of a
     * stock map, and a rectangle per tile is far too slow.
     */
    private updatePropertyOverlay(bounds: TileBounds): void {
        const snap = (value: number): number => Math.floor(value / OVERLAY_SNAP_TILES) * OVERLAY_SNAP_TILES;
        const minX = Math.max(0, snap(bounds.minX) - OVERLAY_SNAP_TILES);
        const minY = Math.max(0, snap(bounds.minY) - OVERLAY_SNAP_TILES);
        const maxX = Math.min(this.mapWidth - 1, snap(bounds.maxX) + 2 * OVERLAY_SNAP_TILES);
        const maxY = Math.min(this.mapHeight - 1, snap(bounds.maxY) + 2 * OVERLAY_SNAP_TILES);

        const boundsKey = `${minX},${minY},${maxX},${maxY}`;
        if (!this.overlayDirty && boundsKey === this.lastOverlayBounds) return;
        this.overlayDirty = false;
        this.lastOverlayBounds = boundsKey;

        this.propertyOverlay.clear();

        for (const style of OVERLAY_STYLES) {
            if (!this.overlayVisibility[style.key]) continue;

            let hasRuns = false;
            for (let y = minY; y <= maxY; y++) {
                let runStart = -1;
                for (let x = minX; x <= maxX + 1; x++) {
                    const tile = x <= maxX ? this.options.getOverlayTile(x, y) : null;
                    const flagged = tile !== null && overlayTileHas(tile, style.key);

                    if (flagged && runStart < 0) {
                        runStart = x;
                    } else if (!flagged && runStart >= 0) {
                        this.propertyOverlay.rect(
                            runStart * TILE_SIZE,
                            y * TILE_SIZE,
                            (x - runStart) * TILE_SIZE,
                            TILE_SIZE
                        );
                        runStart = -1;
                        hasRuns = true;
                    }
                }
            }

            if (hasRuns) {
                this.propertyOverlay.fill({ color: style.color, alpha: style.alpha });
            }
        }
    }

    /**
     * Show the selection rectangle (inclusive tile bounds), or hide it
     */
    setSelection(bounds: { left: number; top: number; right: number; bottom: number } | null): void {
        this.selectionOverlay.clear();
        if (!bounds) return;

        this.selectionOverlay.rect(
            bounds.left * TILE_SIZE,
            bounds.top * TILE_SIZE,
            (bounds.right - bounds.left + 1) * TILE_SIZE,
            (bounds.bottom - bounds.top + 1) * TILE_SIZE
        );
        this.selectionOverlay.fill({ color: 0x6366f1, alpha: 0.2 });
        this.selectionOverlay.stroke({ width: 2, color: 0xa5b4fc, alpha: 0.95, pixelLine: true });
    }

    /**
     * Update cursor overlay (brush preview)
     * @param brushTiles - Array of tiles with offset, texture, and optional pivot for objects
     */
    updateCursor(tileX: number, tileY: number, brushTiles: CursorTile[] = []): void {
        this.cursorOverlay.clear();

        // Clear preview sprites
        for (const sprite of this.cursorPreviewSprites) {
            sprite.visible = false;
        }

        if (tileX < 0 || tileY < 0 || tileX >= this.mapWidth || tileY >= this.mapHeight) {
            return;
        }

        // Check if any brush tile has a texture for preview
        const hasTexturePreview = brushTiles.some(t => t.texture);

        // Render texture previews for each brush tile
        let spriteIndex = 0;
        for (const tile of brushTiles) {
            if (!tile.texture) continue;

            const x = tileX + tile.offsetX;
            const y = tileY + tile.offsetY;

            if (x >= 0 && x < this.mapWidth && y >= 0 && y < this.mapHeight) {
                // Get or create a preview sprite
                let sprite: Sprite;
                if (spriteIndex < this.cursorPreviewSprites.length) {
                    sprite = this.cursorPreviewSprites[spriteIndex];
                } else {
                    sprite = new Sprite();
                    this.cursorPreviewContainer.addChild(sprite);
                    this.cursorPreviewSprites.push(sprite);
                }

                sprite.texture = tile.texture;
                sprite.x = x * TILE_SIZE + (tile.pivotX ?? 0);
                sprite.y = y * TILE_SIZE + (tile.pivotY ?? 0);
                sprite.visible = true;
                spriteIndex++;
            }
        }

        if (brushTiles.length === 0) {
            // Single tile mode - no brush selected
            this.cursorOverlay.rect(tileX * TILE_SIZE, tileY * TILE_SIZE, TILE_SIZE, TILE_SIZE);
        } else {
            // Draw outline for each brush tile
            for (const tile of brushTiles) {
                const x = tileX + tile.offsetX;
                const y = tileY + tile.offsetY;

                if (x >= 0 && x < this.mapWidth && y >= 0 && y < this.mapHeight) {
                    this.cursorOverlay.rect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
                }
            }
        }

        // More subtle when showing a texture preview
        const outlineAlpha = hasTexturePreview ? 0.4 : 0.8;
        this.cursorOverlay.stroke({ width: 2, color: 0xffffff, alpha: outlineAlpha });
    }

    /**
     * Set grid visibility
     */
    setShowGrid(show: boolean): void {
        this.showGrid = show;
        this.updateGrid();
    }

    /**
     * Set object visibility
     */
    setShowObjects(show: boolean): void {
        this.objectLayer.visible = show;
    }

    // ---- View -------------------------------------------------------------

    get zoom(): number {
        return this._zoom;
    }

    get panX(): number {
        return this._panX;
    }

    get panY(): number {
        return this._panY;
    }

    /**
     * Move the view without reporting it back through onViewChange
     */
    setView(panX: number, panY: number, zoom: number): void {
        const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));

        if (clampedZoom !== this._zoom) {
            // Zoom requested from outside (toolbar, shortcuts) keeps the view's
            // center still, so the incoming pan is recomputed around it
            const centerX = this.app.screen.width / 2;
            const centerY = this.app.screen.height / 2;
            const worldX = (centerX - this._panX) / this._zoom;
            const worldY = (centerY - this._panY) / this._zoom;
            this._zoom = clampedZoom;
            this._panX = centerX - worldX * clampedZoom;
            this._panY = centerY - worldY * clampedZoom;
            this.applyView(true);
            return;
        }

        this._panX = panX;
        this._panY = panY;
        this.applyView(false);
    }

    /**
     * Zoom so the world point under a screen position stays under it
     */
    private zoomAt(zoom: number, screenX: number, screenY: number): void {
        const worldX = (screenX - this._panX) / this._zoom;
        const worldY = (screenY - this._panY) / this._zoom;

        this._zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
        this._panX = screenX - worldX * this._zoom;
        this._panY = screenY - worldY * this._zoom;
        this.applyView(true);
    }

    /**
     * Center view on tile
     */
    centerOn(tileX: number, tileY: number): void {
        this._panX = this.app.screen.width / 2 - tileX * TILE_SIZE * this._zoom;
        this._panY = this.app.screen.height / 2 - tileY * TILE_SIZE * this._zoom;
        this.applyView(true);
    }

    private applyView(notify: boolean): void {
        this.worldContainer.scale.set(this._zoom);
        this.worldContainer.x = this._panX;
        this.worldContainer.y = this._panY;

        if (notify) {
            this.options.onViewChange?.(this._panX, this._panY, this._zoom);
        }
    }

    /**
     * Get the tiles currently on screen, clamped to the map
     */
    getVisibleBounds(): TileBounds {
        const topLeft = this.screenToTile(0, 0);
        const bottomRight = this.screenToTile(this.app.screen.width, this.app.screen.height);

        return {
            minX: Math.max(0, Math.min(this.mapWidth - 1, topLeft.x)),
            minY: Math.max(0, Math.min(this.mapHeight - 1, topLeft.y)),
            maxX: Math.max(0, Math.min(this.mapWidth - 1, bottomRight.x)),
            maxY: Math.max(0, Math.min(this.mapHeight - 1, bottomRight.y)),
        };
    }

    /**
     * Convert screen coordinates to tile coordinates
     */
    screenToTile(screenX: number, screenY: number): { x: number; y: number } {
        const worldX = (screenX - this._panX) / this._zoom;
        const worldY = (screenY - this._panY) / this._zoom;

        return {
            x: Math.floor(worldX / TILE_SIZE),
            y: Math.floor(worldY / TILE_SIZE),
        };
    }

    /**
     * While pan mode is on (space held), left-drag pans instead of editing
     */
    setPanMode(enabled: boolean): void {
        this.panMode = enabled;
        this.updateCanvasCursor();
    }

    private updateCanvasCursor(): void {
        if (this.destroyed) return;
        this.app.canvas.style.cursor = this.isPanning ? 'grabbing' : this.panMode ? 'grab' : 'default';
    }

    /**
     * Per-frame update: everything here depends on what is on screen
     */
    private update(): void {
        if (this.mapWidth === 0 || this.mapHeight === 0) return;

        this.frame++;
        const bounds = this.getVisibleBounds();

        this.updateChunks(bounds);
        this.updateObjectRows(bounds);
        this.updatePropertyOverlay(bounds);
    }

    // ---- Event handlers ---------------------------------------------------

    private handlePointerMove(event: FederatedPointerEvent): void {
        const { x, y } = event.global;

        // A button released outside the browser window never sends pointerup.
        // Without this the drag would stay live and paint from hover movement.
        if ((this.isDragging || this.isPanning) && event.buttons === 0) {
            this.endInteraction();
        }

        if (this.isPanning) {
            this._panX += x - this.lastMouseX;
            this._panY += y - this.lastMouseY;
            this.applyView(true);
        } else {
            // Only update tile hover when not panning
            const tile = this.screenToTile(x, y);
            this.options.onTileHover?.(tile.x, tile.y);

            if (this.isDragging && (tile.x !== this.lastDragTileX || tile.y !== this.lastDragTileY)) {
                this.lastDragTileX = tile.x;
                this.lastDragTileY = tile.y;
                this.options.onDrag?.(tile.x, tile.y);
            }
        }

        this.lastMouseX = x;
        this.lastMouseY = y;
    }

    private handlePointerDown(event: FederatedPointerEvent): void {
        const { x, y } = event.global;
        this.lastMouseX = x;
        this.lastMouseY = y;

        // Middle button, space+drag or ctrl+drag pans
        if (event.button === 1 || (event.button === 0 && (event.ctrlKey || this.panMode))) {
            this.isPanning = true;
            this.updateCanvasCursor();
            return;
        }

        const tile = this.screenToTile(x, y);

        // Only the left button edits - right-click is the eyedropper
        if (event.button === 0) {
            this.isDragging = true;
            this.lastDragTileX = tile.x;
            this.lastDragTileY = tile.y;
            this.options.onDragStart?.(tile.x, tile.y);
        } else if (event.button === 2) {
            this.options.onTileRightClick?.(tile.x, tile.y);
        }
    }

    private handlePointerUp(): void {
        this.endInteraction();
    }

    /**
     * End any pan or drag in progress. Reached from pointerup, and from the
     * cases where pointerup is lost: window blur, or a move with no buttons.
     */
    private endInteraction(): void {
        if (this.isPanning) {
            this.isPanning = false;
            this.updateCanvasCursor();
        }

        if (this.isDragging) {
            this.isDragging = false;
            this.options.onDragEnd?.(this.lastDragTileX, this.lastDragTileY);
        }
    }

    private handlePointerLeave(): void {
        if (!this.isDragging && !this.isPanning) {
            this.options.onTileHover?.(-1, -1);
        }
    }

    private handleWheel(event: WheelEvent): void {
        event.preventDefault();
        if (this.destroyed) return;

        // Get mouse position relative to canvas
        const rect = this.app.canvas.getBoundingClientRect();
        const zoomDelta = event.deltaY > 0 ? 0.9 : 1.1;
        this.zoomAt(this._zoom * zoomDelta, event.clientX - rect.left, event.clientY - rect.top);
    }

    /**
     * Destroy the renderer
     */
    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;

        this.resizeObserver?.disconnect();
        this.resizeObserver = null;

        this.app.canvas.removeEventListener('wheel', this.handleWheelBound);
        this.app.canvas.removeEventListener('contextmenu', this.handleContextMenuBound);
        this.app.canvas.removeEventListener('pointerleave', this.handlePointerLeaveBound);
        window.removeEventListener('blur', this.handleWindowBlurBound);
        this.app.ticker.remove(this.update, this);

        for (const chunk of this.chunks.values()) {
            chunk.texture.destroy(true);
        }
        for (const texture of this.freeChunkTextures) {
            texture.destroy(true);
        }
        this.chunks.clear();
        this.freeChunkTextures = [];
        this.bakeContainer.destroy({ children: true });

        this.app.destroy(true, { children: true });
    }
}
